import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";

const ResumePreview = ({ resumeData }) => {
  const resumeRef = useRef();

  // Generate PDF
  const handleDownloadPDF = useReactToPrint({
    content: () => resumeRef.current,
    documentTitle: "My_Resume",
    contentRef: resumeRef,
  });

  return (
    <div className="w-full max-w-[4in] min-w-[4in] bg-white shadow-lg p-4 text-left">
      <h2 className="text-lg font-bold mb-4 p-2">Live Resume Preview</h2>

      {/* Scrollable Resume Preview */}
      <div className="border rounded-lg overflow-hidden flex justify-center items-center">
        <div
          ref={resumeRef}
          className="border p-2 rounded-lg text-gray-900 w-full max-h-[70vh] overflow-auto"
        >
          {/* Personal Info */}
          <div className="mb-4">
            <h1 className="text-xl font-bold">
              {resumeData.personal?.fullName || "First Last"}
            </h1>
            <p className="text-gray-600 text-sm">
              {resumeData.personal?.email || "first.last@email.com"} |{" "}
              {resumeData.personal?.phone || "+1 (123) 456-7890"} |{" "}
              {resumeData.personal?.location || "San Francisco, CA"}
            </p>
          </div>

          {/* Experience Section */}
          {resumeData.experience?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-md font-semibold border-b pb-1 uppercase">
                Experience
              </h3>
              {resumeData.experience.map((exp, index) => (
                <div key={index} className="mt-3">
                  <h4 className="font-semibold text-sm">
                    {exp.company || "Company Name"}{" "}
                    <span className="text-gray-500 text-xs">
                      | {exp.location || "Location"}
                    </span>
                  </h4>
                  <p className="italic text-gray-600 text-xs">
                    {exp.title || "Job Title"} | {exp.startDate || "Start Date"}{" "}
                    - {exp.stillWorking ? "Present" : exp.endDate || "End Date"}
                  </p>
                  <ul className="list-disc pl-4 text-gray-700 text-xs">
                    {exp.description ? (
                      exp.description
                        .split("\n")
                        .map((line, i) => <li key={i}>{line}</li>)
                    ) : (
                      <li>Job details...</li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* Education Section */}
          {resumeData.education?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-md font-semibold border-b pb-1 uppercase">
                Education
              </h3>
              {resumeData.education.map((edu, index) => (
                <div key={index} className="mt-3">
                  <h4 className="font-semibold text-sm">
                    {edu.institution || "University Name"}{" "}
                    <span className="text-gray-500 text-xs">
                      | {edu.location || "Location"}
                    </span>
                  </h4>
                  <p className="italic text-gray-600 text-xs">
                    {edu.degree || "Degree"} | {edu.graduationYear || "Year"}
                  </p>
                  {edu.description && (
                    <p className="text-gray-700 text-xs">{edu.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Other Sections: Skills, Certifications, Languages */}
          {(resumeData.skills?.length > 0 ||
            resumeData.certificates?.length > 0 ||
            resumeData.languages?.length > 0) && (
            <div className="mt-6">
              <h3 className="text-md font-semibold border-b pb-1 uppercase">
                Other
              </h3>
              <ul className="list-disc pl-4 text-gray-700 text-xs">
                {resumeData.skills?.length > 0 && (
                  <li>
                    <span className="font-semibold">Skills:</span>{" "}
                    {resumeData.skills.map((skill) => skill.trim()).join(", ")}
                  </li>
                )}

                {resumeData.certificates?.length > 0 && (
                  <li>
                    <span className="font-semibold">
                      Certifications & Training:
                    </span>{" "}
                    {resumeData.certificates
                      .map((cert) => cert.title.trim())
                      .join(", ")}
                  </li>
                )}

                {resumeData.languages?.length > 0 && (
                  <li>
                    <span className="font-semibold">Languages:</span>{" "}
                    {resumeData.languages
                      .map(
                        (lang) =>
                          `${lang.name.trim()} (${lang.proficiency
                            .toLowerCase()
                            .trim()})`
                      )
                      .join(", ")}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Download PDF Button */}
      <button
        onClick={handleDownloadPDF}
        className="mt-2 px-6 py-2 bg-green-600 text-white rounded-lg w-full text-sm"
      >
        Download PDF
      </button>
    </div>
  );
};

export default ResumePreview;
